/**
 * Customer Table Component
 *
 * Displays customer portfolio data with segmentation flags.
 * Shows revenue, change rates, and customer flags.
 */

import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { Badge } from "@/frontend/components/ui/badge";
import { Button } from "@/frontend/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { TrendingUp, TrendingDown, Star, Users } from "lucide-react";
import type { CustomerInfo } from "@/shared/types/sales-analytics";
import { t } from "@/shared/lib/th-translations";

interface CustomerTableProps {
  customers: CustomerInfo[];
  title?: string;
  description?: string;
}

export function CustomerTable({ customers, title = t("customerTable.title"), description }: CustomerTableProps) {
  const navigate = useNavigate();

  if (customers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            {t("customerTable.noData")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Current Revenue</TableHead>
                <TableHead className="text-right">Previous Revenue</TableHead>
                <TableHead className="text-right">Change</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => {
                const isPositive = customer.changeRate.revenueChangePercentage > 0;
                const isNegative = customer.changeRate.revenueChangePercentage < 0;

                return (
                  <TableRow key={customer.customerId}>
                    <TableCell className="font-medium">
                      <Button
                        variant="link"
                        className="p-0 h-auto font-normal"
                        onClick={() => navigate(`/analytics/customers/${customer.customerId}`)}
                      >
                        {customer.customerName}
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      ฿{customer.currentPeriod.revenue.toLocaleString()}
                      <div className="text-xs text-muted-foreground">
                        {customer.currentPeriod.projectCount} {t("customerTable.table.projects")}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      ฿{customer.previousPeriod.revenue.toLocaleString()}
                      <div className="text-xs text-muted-foreground">
                        {customer.previousPeriod.projectCount} {t("customerTable.table.projects")}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`flex items-center justify-end gap-1 ${
                        isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {isPositive ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : isNegative ? (
                          <TrendingDown className="h-4 w-4" />
                        ) : null}
                        <span className="font-semibold">
                          {customer.changeRate.revenueChangePercentage.toFixed(1)}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {customer.topCategories && customer.topCategories.length > 0 ? (
                          customer.topCategories.slice(0, 3).map((category) => (
                            <Badge key={category} variant="outline" className="text-xs">
                              {category}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">N/A</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {customer.flags.isNew && (
                          <Badge variant="default" className="text-xs bg-blue-600">
                            <Users className="h-3 w-3 mr-1" />
                            New
                          </Badge>
                        )}
                        {customer.flags.isHighImpact && (
                          <Badge variant="default" className="text-xs bg-yellow-600">
                            <Star className="h-3 w-3 mr-1" />
                            High Impact
                          </Badge>
                        )}
                        {customer.flags.isChurned && (
                          <Badge variant="destructive" className="text-xs">
                            Churned
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
